import { useEffect, useRef } from 'react';
import type { MailListItem, MailView } from '@jerome2/shared';
import { listDate, orgOf } from '../lib/format.ts';
import { itemKey, matchesSel } from '../state/mail.ts';
import { useLongPress } from '../lib/longpress.ts';
import { mailVerbs } from './verbs.ts';
import { Icon } from '../ui/Icon.tsx';
import { Button, Spinner, Strip } from '../ui/primitives.tsx';
import { statusTone } from '../shell/WorkersRail.tsx';

// De maillijst: afzender, onderwerp (serif), voorbeeldregel; bij Verwerkt is elk item een taak
// (een mail met meerdere acties staat er meerdere keren) met een kleurstrook, de status en de
// instructie; bij "urgentste eerst" de urgentie als stip met reden. Het vlaggetje is een knop
// (klik = vlag weg, of vlaggen bij hover), rechtsklik opent het actiemenu, en een mail die in de
// einde-dag-samenvatting zit draagt een zonnetje.

export function taskLine(item: MailListItem): { text: string; tone: 'blue' | 'amber' | 'green' | 'red' | 'grey' } | null {
  const t = item.task;
  if (!t) return null;
  switch (t.status) {
    case 'queued':
      return { text: 'In de wachtrij', tone: 'grey' };
    case 'running':
      return { text: `Bezig: ${t.progress ?? 'gestart'}`, tone: 'grey' };
    case 'question':
      return { text: 'Vraag aan jou', tone: 'amber' };
    case 'done':
      return { text: t.resultType === 'reply' ? 'Concept klaar' : t.resultType === 'sign' ? 'Ondertekend en gecontroleerd' : t.resultType === 'summary' ? 'Samenvatting klaar' : t.resultType === 'research' ? 'Onderzoek klaar' : t.resultType === 'agenda' ? 'Afspraak voorgesteld' : 'Klaar', tone: t.resultType === 'sign' ? 'green' : 'blue' };
    case 'failed':
      return { text: 'Mislukt', tone: 'red' };
    default:
      return null;
  }
}

const URGENCY_TONE: Record<number, string> = { 5: 'bg-red', 4: 'bg-amber', 3: 'bg-accent', 2: 'bg-line-strong', 1: 'bg-line' };

export function MailList(props: { items: MailListItem[]; view: MailView; selectedId: string | null; selectedTaskId?: string | null | undefined; onSelect: (id: string, taskId?: string | null) => void; loading: boolean; error: string | null; more: boolean; onMore: () => void; onRetry: () => void; showUrgency: boolean; now?: Date | undefined; onContextMenu?: ((id: string, x: number, y: number) => void) | undefined; onFlag?: ((id: string, flagged: boolean) => void) | undefined; /** Naam van de lijst voor hulpmiddelen (standaard "Mail"; het blok bovenaan heet "Klaar voor jou"). */ label?: string | undefined }) {
  const ref = useRef<HTMLUListElement>(null);
  const sel = props.selectedId ? { id: props.selectedId, taskId: props.selectedTaskId ?? null } : null;
  // Bij Verwerkt (en het blok "Klaar voor jou") kiest een klik de taak van het item; elders alleen de mail.
  const withTask = props.view === 'processed';
  const selectedKey = sel ? (props.items.find((i) => matchesSel(i, sel, props.view)) ?? null) : null;
  // Lang indrukken op een aanraakscherm is de rechtsklik van de telefoon: het opent het actiemenu (daar
  // een vel) zonder de mail te openen; één hook op de hele lijst, de rij volgt uit het doelelement.
  const press = useLongPress((x, y, target) => {
    const key = target.closest<HTMLElement>('li[data-key]')?.dataset['key'];
    const item = key ? props.items.find((i) => itemKey(i, props.view) === key) : undefined;
    if (item && props.onContextMenu) props.onContextMenu(item.id, x, y);
  });
  useEffect(() => {
    if (!selectedKey || !ref.current) return;
    const el = ref.current.querySelector<HTMLElement>(`[data-key="${CSS.escape(itemKey(selectedKey, props.view))}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedKey, props.view]);

  if (props.error && !props.items.length) {
    return (
      <div className="p-6 text-center text-[13px]">
        <p className="text-red mb-3">{props.error}</p>
        <Button size="sm" onClick={props.onRetry} icon="refresh">
          Opnieuw proberen
        </Button>
      </div>
    );
  }
  if (!props.items.length) {
    if (props.loading) return <Spinner className="p-6" />;
    const text = props.view === 'flagged' ? 'Geen gevlagde mail. Lekker.' : props.view === 'unread' ? 'Alles gelezen.' : props.view === 'processed' ? 'Nog niets verwerkt. Geef een mail een instructie.' : props.view === 'search' ? 'Niets gevonden.' : 'Deze map is leeg.';
    return <div className="p-8 text-center text-muted">{text}</div>;
  }
  return (
    <div className="h-full flex flex-col">
      <ul ref={ref} {...press} className="flex-1 min-h-0 overflow-auto scroll-thin" role="listbox" aria-label={props.label ?? 'Mail'}>
        {props.items.map((m) => {
          const selected = matchesSel(m, sel, props.view);
          const line = props.view === 'processed' ? taskLine(m) : null;
          const org = m.from ? orgOf(m.from.address) : '';
          const key = itemKey(m, props.view);
          const verbs = mailVerbs(m);
          return (
            <li
              key={key}
              data-id={m.id}
              data-key={key}
              role="option"
              aria-selected={selected}
              tabIndex={-1}
              onClick={() => props.onSelect(m.id, withTask ? (m.task?.id ?? null) : null)}
              onContextMenu={(e) => {
                if (!props.onContextMenu) return;
                e.preventDefault();
                props.onSelect(m.id, withTask ? (m.task?.id ?? null) : null);
                props.onContextMenu(m.id, e.clientX, e.clientY);
              }}
              className={`group relative cursor-default border-b border-line px-4 py-3 no-callout pointer-coarse:min-h-16 ${selected ? 'bg-tint' : 'hover:bg-paper'}`}
            >
              {line ? <Strip tone={line.tone} /> : null}
              <div className="flex items-center gap-2 min-w-0">
                {props.view !== 'processed' ? (
                  <button
                    type="button"
                    aria-label={m.flagged ? 'Vlag weg' : 'Vlaggen'}
                    title={m.flagged ? 'Vlag weg' : 'Vlaggen'}
                    className={`shrink-0 -ml-1 h-6 w-6 pointer-coarse:h-11 pointer-coarse:w-11 pointer-coarse:-ml-3 pointer-coarse:-my-2 rounded flex items-center justify-center transition-opacity ${m.flagged ? 'text-red hover:bg-red-soft' : 'text-line-strong opacity-0 group-hover:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100 hover:text-red hover:bg-tint'}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      props.onFlag?.(m.id, !m.flagged);
                    }}
                  >
                    <Icon name="flag" size={13} />
                  </button>
                ) : null}
                {props.showUrgency ? <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${m.urgency ? URGENCY_TONE[m.urgency] : 'bg-line'}`} title={m.urgency ? `Urgentie ${m.urgency}/5: ${m.urgencyReason ?? ''}` : 'Nog niet beoordeeld'} /> : null}
                <span className={`truncate ${m.isRead ? 'font-medium' : 'font-semibold'} text-ink`}>{m.from?.name || m.from?.address || 'Onbekend'}</span>
                {org ? <span className="truncate text-muted text-[13px] hidden xl:inline">{org}</span> : null}
                {verbs.replied !== null ? (
                  <span className="shrink-0 text-accent" title={verbs.replied ? `Beantwoord op ${listDate(verbs.replied, props.now)}` : 'Beantwoord'} aria-label="Beantwoord">
                    <Icon name="reply" size={13} />
                  </span>
                ) : null}
                {verbs.forwarded !== null ? (
                  <span className="shrink-0 text-accent" title={verbs.forwarded ? `Doorgestuurd op ${listDate(verbs.forwarded, props.now)}` : 'Doorgestuurd'} aria-label="Doorgestuurd">
                    <Icon name="forward" size={13} />
                  </span>
                ) : null}
                {m.isDraft ? (
                  <span className="shrink-0 inline-flex items-center gap-1 rounded px-1 text-[11px] font-medium text-accent bg-tint" title="Concept: nog niet verstuurd">
                    <Icon name="pen" size={11} /> Concept
                  </span>
                ) : null}
                {m.eodDay ? (
                  <span className="ml-auto shrink-0 inline-flex items-center gap-1 rounded px-1 text-[11px] font-medium text-amber bg-amber-soft" title={`In de einde-dag-samenvatting van ${m.eodDay}`}>
                    <Icon name="sun" size={11} /> Einde dag
                  </span>
                ) : null}
                <span className={`${m.eodDay ? '' : 'ml-auto'} shrink-0 text-[12px] text-muted tabular-nums`}>{listDate(m.received, props.now)}</span>
              </div>
              <div className={`font-display text-[16px] leading-snug truncate ${m.isRead ? 'text-ink' : 'text-ink font-medium'}`}>{m.subject || '(geen onderwerp)'}</div>
              {line ? (
                <div className={`flex items-center gap-1.5 text-[13px] min-w-0 ${line.tone === 'blue' ? 'text-accent' : line.tone === 'amber' ? 'text-amber' : line.tone === 'green' ? 'text-green' : line.tone === 'red' ? 'text-red' : 'text-muted'}`}>
                  <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${line.tone === 'blue' ? 'bg-accent' : line.tone === 'amber' ? 'bg-amber' : line.tone === 'green' ? 'bg-green' : line.tone === 'red' ? 'bg-red' : 'bg-line-strong'}`} />
                  <span className="truncate shrink-0 max-w-[60%]">{line.text}</span>
                  {m.task ? (
                    <span className="truncate min-w-0 text-muted" title={m.task.instruction}>
                      · {m.task.split ? `deel ${m.task.split.index + 1}/${m.task.split.total}: ` : ''}{m.task.instruction}
                    </span>
                  ) : null}
                </div>
              ) : (
                <div className="flex items-center gap-1 text-[13px] text-muted truncate">
                  {m.hasAttachments ? <Icon name="clip" size={12} className="shrink-0" /> : null}
                  <span className="truncate">{m.preview || ' '}</span>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {props.more ? (
        <div className="shrink-0 p-2 border-t border-line text-center">
          <Button size="sm" variant="ghost" onClick={props.onMore} busy={props.loading}>
            Meer laden
          </Button>
        </div>
      ) : props.loading ? (
        <div className="shrink-0 p-2 border-t border-line"><Spinner className="justify-center text-[13px]" label="Bijwerken…" /></div>
      ) : null}
    </div>
  );
}
